import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAIAssistant } from "./AIAssistantProvider";
import ImageSourcePicker from "../../prof/presentationTitles/ImageSourcePicker";
import {
  getPreferOwnImages,
  getPresentationImageSource,
  setPreferOwnImages,
  setPresentationImageSource,
  subscribePresentationImageSource,
} from "../../prof/presentationTitles/imageSourcePreference";
import AIChatHeader from "./AIChatHeader";
import AIChatMessages from "./AIChatMessages";
import AIChatInput from "./AIChatInput";
import Button from "@mui/material/Button";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import AIErrorMessage from "./AIErrorMessage";
import AIConversationHistory from "./AIConversationHistory";
import AIConversationLimitBanner from "./AIConversationLimitBanner";
import QuizSuccessCard from "./quiz/QuizSuccessCard";
import QuizDraftCard from "./quiz/QuizDraftCard";
import QuizTitlePickerCard from "./quiz/QuizTitlePickerCard";
import GeneratedQuestionsCard from "./quiz/GeneratedQuestionsCard";
import PresentationDraftCard from "./quiz/PresentationDraftCard";
import DocumentDraftCard from "./drafts/DocumentDraftCard";
import { useQuizDraftOptional } from "./quiz/QuizDraftContext";
import { AI_ASSISTANT_BLUE, AI_PANEL_WIDTH_CSS } from "./constants";
import { optionsForLane } from "./assistantIntent";

const PANEL_BOTTOM_GAP = 24; // px — keeps input clearly above the screen edge

export default function AIAssistantPanel() {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const [imageSource, setImageSource] = useState(getPresentationImageSource);
  const [preferOwnImages, setPreferOwnImagesState] = useState(getPreferOwnImages);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const quizDraft = useQuizDraftOptional()?.draft || null;
  useEffect(() => subscribePresentationImageSource((next) => {
    setImageSource(next);
    setPreferOwnImagesState(getPreferOwnImages());
  }), []);
  const {
    isOpen,
    close,
    messages,
    draft,
    setDraft,
    sendMessage,
    retryMessage,
    sourceAttachment,
    attachSourceFile,
    clearSourceAttachment,
    sending,
    restoring,
    error,
    clearError,
    routeContext,
    startNewConversation,
    conversationLimitReached,
    copilotUi,
    confirmCreateQuiz,
    editQuizConfiguration,
    confirmPresentationDraft,
    discardPresentationDraft,
    updatePresentationSlide,
    removePresentationSlide,
    removePresentationSlide3d,
    duplicatePresentationSlide,
    movePresentationSlide,
    addPresentationSlide,
    setPresentationSlideVisual,
    clearPresentationSlideImage,
    setPresentationSlideTextOnly,
    rewritePresentationSlide,
    searchPresentationLibrary,
    addPresentationLibrarySlide,
    openQuizFullForm,
    updateQuizSettings,
    updateQuizRule,
    removeQuizRule,
    setQuizLockedRefs,
    updateGeneratedQuestion,
    removeGeneratedQuestion,
    saveGeneratedQuestions,
    discardGeneratedQuestions,
    openTitlePicker,
    closeTitlePicker,
    loadQuizTitleOptions,
    applyQuizTitles,
    documentDraft,
    updateDocumentSection,
    removeDocumentSection,
    addDocumentSection,
    moveDocumentSection,
    rewriteDocumentSection,
    saveDocumentDraft,
    discardDocumentChanges,
    closeDocumentDraft,
    applyDocumentTo,
    undoStack,
    undoDraftEdit,
    history,
    openHistory,
    closeHistory,
    refreshHistory,
    openConversation,
    renameConversation,
    deleteConversation,
    conversationId,
    conversationTitle,
    limit,
    openCreatedQuiz,
    openCreatedQuizResults,
  } = useAIAssistant();
  const showImageSources =
    routeContext?.key === "presentation_maker" || routeContext?.key === "presentations";

  // At the limit the text stays in the input so it can be carried to a new conversation.
  const handleSend = () => {
    if (conversationLimitReached) return;
    const text = String(draft || "").trim();
    if (!text && sourceAttachment?.status !== "ready") return;
    sendMessage(text);
  };

  const handleQuickAction = (action) => {
    if (conversationLimitReached) return;
    const id = typeof action === "string" ? "" : String(action?.id || "");
    const prompt = typeof action === "string" ? action : action?.prompt || action?.label;
    if (id === "presentation_confirm") {
      confirmPresentationDraft();
      return;
    }
    if (id === "presentation_discard") {
      discardPresentationDraft();
      return;
    }
    if (id === "quiz_confirm") {
      confirmCreateQuiz();
      return;
    }
    if (id === "quiz_configure") {
      editQuizConfiguration();
      return;
    }
    if (!prompt) return;
    sendMessage(prompt);
  };

  const handleDeckAction = (action, payload) => {
    if (conversationLimitReached) return;
    if (action === "delete-slide") {
      removePresentationSlide(payload);
      return;
    }
    if (action === "save-slide") {
      updatePresentationSlide(payload?.index, payload);
      return;
    }
    if (action === "discard") {
      discardPresentationDraft();
      return;
    }
    if (action === "make-presentation") {
      confirmPresentationDraft();
    }
  };

  const handleStarterSelect = (action) => {
    if (!action || conversationLimitReached) return;
    sendMessage(action.prompt || action.label);
  };

  const dynamicOptions = (copilotUi?.optionChips || []).map((opt) => {
    const id = String(opt.id || "");
    let prompt = opt.prompt;
    if (!prompt) {
      if (id === "open_results" || id === "open-results") prompt = "__open_results__";
      else if (id === "start_quiz" || id === "create-quiz") prompt = "Create a quiz";
      else if (
        id === "my_results" ||
        id === "check-quiz" ||
        id === "refresh_results" ||
        id === "retry-results"
      ) {
        prompt = "Analyze my student quiz results again";
      } else if (id === "ask-quizzes") prompt = "Help me with my quizzes and question bank";
      else if (id === "bank:private") prompt = "Use my private question bank";
      else if (id === "use_microlab") prompt = "Use Microlab public bank";
      else if (
        id.startsWith("cat:") ||
        id.startsWith("topic:") ||
        id.startsWith("use_count:") ||
        id.startsWith("guide:") ||
        id.startsWith("presentation_remove_slide:") ||
        id.startsWith("presentation_update_slide:") ||
        id.startsWith("presentation_remove_3d:") ||
        [
          "generate_missing",
          "confirm_save_generated",
          "discard_generated",
          "open_question_bank",
          "open_copilot",
          "continue_quiz",
          "change_difficulty",
          "switch_bank",
          "presentation_confirm",
          "presentation_discard",
          "propose_presentation",
          "quiz_confirm",
          "quiz_configure",
        ].includes(id)
      ) {
        prompt = id;
      } else if (id.startsWith("nav:")) {
        prompt = opt.prompt || opt.label;
      } else {
        prompt = opt.label;
      }
    }
    return { id: opt.id, label: opt.label, prompt };
  });

  // Approve / discard / configure live on the draft cards, so chips only carry
  // the assistant's own follow-up suggestions.
  const chatSuggestions = (() => {
    if (conversationLimitReached) return null;
    const cardOwned = new Set([
      "presentation_confirm",
      "presentation_discard",
      "quiz_confirm",
      "quiz_configure",
      "confirm_save_generated",
      "discard_generated",
    ]);
    // Only next steps of what the user asked for: no presentation chips on a quiz ask, etc.
    const list = optionsForLane(dynamicOptions, copilotUi?.activeLane).filter(
      (opt) => !cardOwned.has(String(opt.id || ""))
    );
    return list.length ? list : null;
  })();

  const pending = copilotUi?.pendingPresentation?.slides?.length ? copilotUi.pendingPresentation : null;
  const generated = quizDraft?.pending_generated_questions?.questions?.length
    ? quizDraft.pending_generated_questions
    : null;
  const showQuizCard =
    Boolean(quizDraft?.topic_rules?.length) &&
    Boolean(quizDraft?.awaiting_confirmation || copilotUi?.confirmation || copilotUi?.quizEditorOpen);
  const busy = sending || restoring;
  const confirming = Boolean(copilotUi?.confirming);
  const lastUndo = undoStack?.length ? undoStack[undoStack.length - 1] : null;
  const titlePicker = copilotUi?.titlePicker || null;
  const hasDrafts = Boolean(pending || generated || showQuizCard || documentDraft?.sections?.length);

  // Identity of what the cards show. It changes when the assistant proposes or
  // creates something (not when a card is edited), and the cards then move under
  // the newest reply; otherwise they stay where they were and the chat flows on.
  const workspaceKey = [
    pending ? `deck:${pending.fingerprint || ""}` : "",
    generated ? `gen:${generated.fingerprint || generated.questions.length}` : "",
    showQuizCard
      ? `quiz:${quizDraft?.confirmation_fingerprint || ""}:${JSON.stringify(
          (quizDraft?.topic_rules || []).map((r) => [r.catalogue, r.num_questions])
        )}`
      : "",
    copilotUi?.createdQuiz?.id ? `created:${copilotUi.createdQuiz.id}` : "",
    documentDraft?.source_id ? `doc:${documentDraft.source_id}` : "",
    titlePicker ? `picker:${titlePicker.catalogue || titlePicker.source || ""}` : "",
  ]
    .filter(Boolean)
    .join("|");

  const workspace =
    hasDrafts || lastUndo || copilotUi?.createdQuiz || titlePicker ? (
      <Box>
        {lastUndo ? (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.75 }}>
            <Button
              size="small"
              startIcon={<UndoRoundedIcon fontSize="small" />}
              onClick={undoDraftEdit}
              disabled={busy}
              sx={{ textTransform: "none", fontWeight: 700, fontSize: 12, color: `${AI_ASSISTANT_BLUE} !important` }}
            >
              Undo: {lastUndo.label}
            </Button>
          </Box>
        ) : null}
        {documentDraft ? (
          <DocumentDraftCard
            doc={documentDraft}
            disabled={busy}
            actions={{
              update: updateDocumentSection,
              remove: removeDocumentSection,
              add: addDocumentSection,
              move: moveDocumentSection,
              rewrite: rewriteDocumentSection,
              save: saveDocumentDraft,
              discard: discardDocumentChanges,
              close: closeDocumentDraft,
              useFor: applyDocumentTo,
            }}
          />
        ) : null}
        {pending ? (
          <PresentationDraftCard
            pending={pending}
            confirming={confirming}
            disabled={busy}
            actions={{
              confirm: confirmPresentationDraft,
              discard: discardPresentationDraft,
              update: updatePresentationSlide,
              remove: removePresentationSlide,
              duplicate: duplicatePresentationSlide,
              move: movePresentationSlide,
              addAfter: addPresentationSlide,
              addMedia: addPresentationLibrarySlide,
              setVisual: setPresentationSlideVisual,
              clearImage: clearPresentationSlideImage,
              clear3d: removePresentationSlide3d,
              textOnly: setPresentationSlideTextOnly,
              rewrite: (index, how) => {
                if (!conversationLimitReached) rewritePresentationSlide(index, how);
              },
              search: searchPresentationLibrary,
            }}
          />
        ) : null}
        {titlePicker ? (
          <QuizTitlePickerCard
            picker={titlePicker}
            disabled={busy}
            onLoad={loadQuizTitleOptions}
            onApply={applyQuizTitles}
            onClose={closeTitlePicker}
          />
        ) : null}
        {showQuizCard ? (
          <QuizDraftCard
            draft={quizDraft}
            confirming={confirming}
            disabled={busy}
            startExpanded={Boolean(copilotUi?.quizEditorOpen)}
            actions={{
              confirm: confirmCreateQuiz,
              updateSettings: updateQuizSettings,
              updateRule: updateQuizRule,
              removeRule: removeQuizRule,
              setLockedRefs: setQuizLockedRefs,
              openFullForm: openQuizFullForm,
              changeTitles: () => {
                const rule = quizDraft?.topic_rules?.[0] || {};
                openTitlePicker({
                  source: rule.bank_source === "private" ? "private" : "microlab",
                  catalogue: rule.catalogue || "histology",
                });
              },
            }}
          />
        ) : null}
        {generated ? (
          <GeneratedQuestionsCard
            pending={generated}
            confirming={confirming}
            disabled={busy}
            actions={{
              save: saveGeneratedQuestions,
              discard: discardGeneratedQuestions,
              update: updateGeneratedQuestion,
              remove: removeGeneratedQuestion,
            }}
          />
        ) : null}
        {copilotUi?.createdQuiz ? (
          <QuizSuccessCard
            quiz={copilotUi.createdQuiz}
            onOpenDetails={openCreatedQuiz}
            onOpenResults={openCreatedQuizResults}
          />
        ) : null}
      </Box>
    ) : null;

  const panelHeight = `calc(100vh - ${PANEL_BOTTOM_GAP}px)`;
  const panelHeightDvh = `calc(100dvh - ${PANEL_BOTTOM_GAP}px)`;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={close}
      variant={isMobile ? "temporary" : "persistent"}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isOpen && !isMobile ? AI_PANEL_WIDTH_CSS : 0,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: isMobile ? "100%" : AI_PANEL_WIDTH_CSS,
          maxWidth: isMobile ? "100%" : 480,
          boxSizing: "border-box",
          borderLeft: "1px solid rgba(148,163,184,0.28)",
          boxShadow: isOpen ? "-12px 0 40px rgba(15, 23, 42, 0.08)" : "none",
          position: "fixed",
          top: 0,
          right: 0,
          // Leave a clear gap above the bottom of the screen.
          bottom: PANEL_BOTTOM_GAP,
          height: panelHeight,
          maxHeight: panelHeight,
          "@supports (height: 100dvh)": {
            height: panelHeightDvh,
            maxHeight: panelHeightDvh,
          },
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.card",
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: isMobile ? 0 : 16,
        },
      }}
    >
      <Box
        onDragEnter={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={(event) => {
          if (event.currentTarget.contains(event.relatedTarget)) return;
          setDragOver(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          const file = event.dataTransfer?.files?.[0];
          if (file) attachSourceFile(file);
        }}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {dragOver ? (
          <Box
            sx={{
              position: "absolute",
              inset: 12,
              zIndex: 5,
              borderRadius: 3,
              border: "2px dashed #2563eb",
              bgcolor: "rgba(239,246,255,0.92)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              color: "#1d4ed8",
              fontWeight: 700,
              textAlign: "center",
              px: 3,
            }}
          >
            Drop a PDF, PowerPoint, text, or Word file
          </Box>
        ) : null}
        <Box sx={{ flexShrink: 0 }}>
          <AIChatHeader
            sectionLabel={routeContext.label}
            conversationTitle={conversationTitle}
            onClose={close}
            onNewConversation={() => startNewConversation({ carryDrafts: false })}
            onToggleHistory={history.open ? closeHistory : openHistory}
            historyOpen={history.open}
            disabled={busy}
            conversationLimitReached={conversationLimitReached}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {history.open ? (
            <AIConversationHistory
              history={history}
              activeId={conversationId}
              onBack={closeHistory}
              onSearch={refreshHistory}
              onOpen={(id) => openConversation(id)}
              onRename={renameConversation}
              onDelete={deleteConversation}
              onNewConversation={() => startNewConversation({ carryDrafts: false })}
            />
          ) : (
          <AIChatMessages
            workspace={workspace}
            workspaceKey={workspaceKey}
            messages={messages}
            sending={sending}
            restoring={restoring}
            sectionLabel={routeContext.label}
            onRetry={conversationLimitReached ? undefined : retryMessage}
            onStarterSelect={handleStarterSelect}
            suggestions={chatSuggestions}
            onSuggestionSelect={handleQuickAction}
            suggestionsDisabled={sending || restoring || copilotUi?.confirming || conversationLimitReached}
            draftSlides={copilotUi?.pendingPresentation?.slides || null}
            onDeckAction={
              copilotUi?.pendingPresentation?.slides?.length ? handleDeckAction : null
            }
          />
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "background.card",
            borderTop: "1px solid rgba(148,163,184,0.18)",
            pb: 1.5,
          }}
        >
          <Box sx={{ pt: 1 }}>
            <AIConversationLimitBanner
              limit={limit}
              hasDrafts={hasDrafts}
              disabled={busy}
              onNewConversation={startNewConversation}
            />
          </Box>
          <AIErrorMessage message={error} onDismiss={clearError} />
          {showImageSources ? (
            <Box sx={{ px: 2, pb: 1.25 }}>
              <Typography sx={{ mb: 0.75, fontSize: 12, fontWeight: 700, color: "ink.body" }}>
                {t("pages.presentationImages.title")}
              </Typography>
              <ImageSourcePicker
                compact
                value={imageSource}
                preferOwn={preferOwnImages}
                onChange={(next) => {
                  setImageSource(next);
                  setPresentationImageSource(next);
                }}
                onPreferOwnChange={(next) => {
                  setPreferOwnImagesState(next);
                  setPreferOwnImages(next);
                }}
              />
            </Box>
          ) : null}
          <AIChatInput
            value={draft}
            onChange={setDraft}
            onSend={handleSend}
            sending={sending}
            disabled={restoring || conversationLimitReached}
            attachment={sourceAttachment}
            onAttach={attachSourceFile}
            onClearAttachment={clearSourceAttachment}
            placeholder={
              conversationLimitReached
                ? "Start a new conversation to send this…"
                : undefined
            }
          />
        </Box>
      </Box>
    </Drawer>
  );
}
